import querySerializer from 'query-string';
import { redirect } from 'redux-first-router';
import { MDTApp } from 'mdt-core-components/src/actions/bootAction';
import { MDTAnalytics } from 'mdt-core-components/src/services/mdtanalytics';
import { bypassNavigationBlock } from 'mdt-core-components/src/actions/blockNavigation';

// NOTE: Find a nav item in the sidebar config, which should be our single source of truth,
// that matches the action type of the Router endpoint 20260413:Alevale
export const findNavItemByActionType = (layoutConfig, actionType) => {
    if (!layoutConfig || typeof layoutConfig !== 'object') {
        return null;
    }

    const normalizedActionType = actionType?.toLowerCase();
    if (!normalizedActionType) {
        return null;
    }

    const entries = Object.values(layoutConfig);
    for (let i = 0; i < entries.length; i++) {
        const item = entries[i];
        if (item && typeof item === 'object') {
            const { link } = item;
            if (
                typeof link === 'string' &&
                link !== '' &&
                link !== 'javascript:void(0)' &&
                link.toLowerCase() === normalizedActionType
            ) {
                return item;
            }

            if (item.submenu && typeof item.submenu === 'object') {
                const found = findNavItemByActionType(item.submenu, actionType);
                if (found) {
                    return found;
                }
            }
        }
    }

    return null;
};

// The purpose of the below options is to demonstrate auth filtering.
// onBeforeChange fires before going to a new route, and you can
// redirect if certian conditions aren't met.

const isAllowed = (type, user, routesMap, meDetails) => {
    const role = routesMap[type] && routesMap[type].role; // you can put arbitrary keys in routes
    const onlyExternal = routesMap[type] && routesMap[type].onlyExternal;
    if (
        onlyExternal &&
        meDetails &&
        meDetails.user &&
        meDetails.user.isinternal.toLowerCase() === 'true'
    ) {
        return false;
    }
    if (!role) return true;
    if (!user) return false; // in a real app, a user isn't always logged in

    return user.roles.includes(role);
};

// NOTE: Returns true when both permission data and feature config have loaded. 20260413:Alevale
const isBootstrapReady = (state) => {
    const featureConfig = state.formConfig?.featureConfiguration;
    return (
        !!state.meDetails?.meState?.HAS_RESOURCE_RIGHTS &&
        !!featureConfig &&
        typeof featureConfig === 'object' &&
        Object.keys(featureConfig).length > 0
    );
};

// NOTE: Replicates navbar-item checkAvailability logic, which evaluates resource
// permissions and roles in various combinations. 20260413:Alevale
const checkAvailability = (available, roles, logicalOperation, getResourceAccess, meRoles) => {
    if (available === undefined && roles === undefined) return true;

    const checkResources = (items) => items.map((i) => getResourceAccess(i.resource, i.action));
    const hasRole = () => roles.some((role) => meRoles.includes(role));

    if (Array.isArray(available) && Array.isArray(roles)) {
        return checkResources(available).every(Boolean) && hasRole();
    }
    if (Array.isArray(available)) {
        const results = checkResources(available);
        return logicalOperation === 'any' ? results.some(Boolean) : results.every(Boolean);
    }
    if (Array.isArray(roles)) {
        if (available && available.resource) {
            return getResourceAccess(available.resource, available.action) && hasRole();
        }
        return hasRole();
    }
    // Single permissions object
    return available ? getResourceAccess(available.resource, available.action) : true;
};

// NOTE: Checks if navigation to a route is permitted, it uses under the hood the navbar
//  show/hide items (checkAvailability + isVisible) in this case to access to the page. 20260413:Alevale
export const isNavigationPermitted = (dispatch, getState, actionType) => {
    const sidebarMain = window.MDTSetting?.layout?.sidebar?.main;

    if (!sidebarMain || Object.keys(sidebarMain).length === 0) {
        return false;
    }

    const { meDetails } = getState();
    if (!meDetails?.meState?.HAS_RESOURCE_RIGHTS) {
        return true;
    }

    const navItem = findNavItemByActionType(sidebarMain, actionType);
    if (!navItem) return true;

    const { available, roles, visible, logicalOperation } = navItem;
    const getResourceAccess = (resource, action) =>
        dispatch(MDTApp.getResourceAccess(resource, action));
    const meRoles = getState().meDetails?.roles || [];

    if (!checkAvailability(available, roles, logicalOperation, getResourceAccess, meRoles)) {
        return false;
    }

    if (typeof visible === 'function') return visible(getResourceAccess, getState());
    if (typeof visible === 'undefined') return true;
    return !!visible;
};

// NOTE: Re-checks route permissions once bootstrap data is fully available.
// onBeforeChange fires during store creation (before permissions load) and must be
// permissive. This subscriber catches the moment both HAS_RESOURCE_RIGHTS and
// featureConfiguration are ready, then unsubscribes from then on,
// onBeforeChange handles the rest. 20260413:Alevale
export const subscribePermissionGuard = (store) => {
    const unsubscribe = store.subscribe(() => {
        const state = store.getState();

        if (isBootstrapReady(state)) {
            unsubscribe();

            const currentType = state.location?.type;
            if (!currentType || currentType === 'HOME' || currentType === 'LOGIN') {
                return;
            }

            const permitted = isNavigationPermitted(store.dispatch, store.getState, currentType);

            if (!permitted) {
                store.dispatch(bypassNavigationBlock(true));
                store.dispatch(redirect({ type: 'HOME' }));
            }
        }
    });

    return unsubscribe;
};

export default {
    querySerializer,
    onBeforeChange: (dispatch, getState, action) => {
        const {
            user,
            location: { routesMap },
            meDetails,
        } = getState();
        // Capturing initial time of any page (Components) load
        window.pageLoadStartTime = new Date().getTime();
        const { type } = action.action;

        const allowed = isAllowed(type, user, routesMap, meDetails);

        // Tracking Pages on Route change.
        MDTAnalytics.track({
            pageInfo: {
                pageName: type,
                pagePath: routesMap[type],
                widgetName: 'N/A',
            },
        });

        dispatch(MDTApp.saveScreenOverlay(false));

        if (!allowed) {
            dispatch(bypassNavigationBlock(true));
            const redirectAction = redirect({ type: 'LOGIN' });
            dispatch(redirectAction);
        }

        // Route-level permission guard only active after bootstrap completes.
        // Skip HOME and LOGIN to prevent infinite redirect loops.
        if (
            allowed &&
            type !== 'HOME' &&
            type !== 'LOGIN' &&
            isBootstrapReady(getState()) &&
            !isNavigationPermitted(dispatch, getState, type)
        ) {
            dispatch(bypassNavigationBlock(true));
            dispatch(redirect({ type: 'HOME' }));
        }
    },
    onAfterChange: (dispatch, getState) => {
        window.scrollTo(0, 0);

        // Reset bypass flag after every route change
        dispatch(bypassNavigationBlock(false));

        const {
            location: { type },
        } = getState();

        if (type === 'LOGIN') {
            // setTimeout(() => alert(alertMessage), 1500)
        }
    },
};
